import Control, { ControlDashboard } from 'models/control';
import ControlContext from 'models/controlContext';
import ControlScene from 'models/controlScene';
import Control_Translation from 'models/control_Translation';
import IdListDTO from 'models/dto/IdListDTO';
import ControlContextDTO from 'models/dto/controlContextDTO';
import ControlDTO, { ControlDashboardDTO } from 'models/dto/controlDTO';
import ControlSceneDTO from 'models/dto/controlSceneDTO';
import Control_TranslationDTO from 'models/dto/control_TranslationDTO';
import NormControlDTO from 'models/dto/normControlDTO';
import NormControl_TranslationDTO from 'models/dto/normControl_TranslationDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import NormControl from 'models/normControl';
import NormControl_Translation from 'models/normControl_Translation';
import { fromApiDateTimeOptional } from 'utils/datetime';
import { mapFromEntity, mapToEntity } from './activityMapping';
import { mapFromDashboard, mapToDashboard } from './dashboardMapping';
import { mapFromISOControls } from './isoControlMapping';
import { mapFromKPIs } from './kpiMapping';
import { mapFromLanguage, mapToLanguage } from './langMapping';
import { mapFromObjectives } from './objectiveMapping';
import { mapFromProcesses } from './processMapping';
import { mapFromProcessingActivities } from './processingActivityMapping';
import { mapFromTasksArray } from './taskMapping';
import { mapFromThemes } from './themeMapping';

export function mapFromControls(controlDTOs: ControlDTO[] | undefined, globalDataCache: GlobalDataCache): Control[] {
  const controlList: Control[] = [];
  if (!controlDTOs) return controlList;

  for (const controlDTO of controlDTOs) {
    const newControl = mapFromControl(controlDTO, globalDataCache);
    controlList.push(newControl as Control);
  }

  return controlList;
}

export function mapFromControl_Translation(
  control_TranslationDTOs: Control_TranslationDTO[] | undefined,
): Control_Translation[] | undefined {
  if (!control_TranslationDTOs) {
    return undefined;
  }

  const control_TranslationList: Control_Translation[] = [];

  for (const control_TranslationDTO of control_TranslationDTOs) {
    const newControl_Translation = new Control_Translation();
    newControl_Translation.controlId = control_TranslationDTO.controlId;
    newControl_Translation.languageId = control_TranslationDTO.languageId;
    newControl_Translation.name = control_TranslationDTO.name;
    newControl_Translation.description = control_TranslationDTO.description;
    newControl_Translation.implementation = control_TranslationDTO.implementation;
    newControl_Translation.background = control_TranslationDTO.background;
    newControl_Translation.outOfScopeReason = control_TranslationDTO.outOfScopeReason;
    newControl_Translation.lang = mapFromLanguage(control_TranslationDTO.lang);
    control_TranslationList.push(newControl_Translation);
  }

  return control_TranslationList;
}

export function mapToControls(controls: Control[]): ControlDTO[] {
  const controlList: ControlDTO[] = [];

  for (const control of controls) {
    const newControlDto = mapToControl(control);
    controlList.push(newControlDto as ControlDTO);
  }

  return controlList;
}

export function mapToControl(control: Control): ControlDTO {
  const newControlDTO = new ControlDTO();

  newControlDTO.tenantId = control.tenantId;
  newControlDTO.controlId = control.controlId;
  newControlDTO.parentControlId = control.parentControlId;
  newControlDTO.code = control.code;
  newControlDTO.groupId = control.groupId;
  newControlDTO.ownerId = control.ownerId;
  newControlDTO.commentTrailId = control.commentTrailId;
  newControlDTO.auditTrailId = control.auditTrailId;
  newControlDTO.state = control.state;
  newControlDTO.applicabilityReason = new IdListDTO(control.applicabilityReason.map((a) => a.toString()));
  newControlDTO.dashboard = control.dashboard ? mapToDashboard(control.dashboard) : undefined;
  newControlDTO.trans = mapToControl_Translation(control.trans);
  newControlDTO.ownerRoleId = control.ownerRoleId;
  newControlDTO.riskIds = control.riskIds ? new IdListDTO(control.riskIds.map((a) => a.toString())) : undefined;
  newControlDTO.updateChildControls = control.updateChildControls;
  newControlDTO.monitoringParent = control.monitoringParent ? mapToEntity(control.monitoringParent) : undefined;
  newControlDTO.unmonitored = control.unmonitored;
  newControlDTO.perNormData = control.perNormData;
  newControlDTO.normControls = control.normControls?.map(mapToNormControl);

  if (newControlDTO.trans && control.transIdx >= 0) {
    newControlDTO.trans[control.transIdx].name = control.name;
    newControlDTO.trans[control.transIdx].description = control.description;
    newControlDTO.trans[control.transIdx].background = control.background;
    newControlDTO.trans[control.transIdx].implementation = control.implementation;
    newControlDTO.trans[control.transIdx].outOfScopeReason = control.outOfScopeReason;
  }

  return newControlDTO;
}

export function mapFromControl(controlDTO: ControlDTO | undefined, cache: GlobalDataCache): Control | undefined {
  if (!controlDTO) {
    return undefined;
  }

  const newControl = new Control();

  newControl.tenantId = controlDTO.tenantId;
  newControl.controlId = controlDTO.controlId;
  newControl.parentControlId = controlDTO.parentControlId;
  newControl.controlType = controlDTO.controlType;
  newControl.code = controlDTO.code;
  newControl.state = controlDTO.state;
  newControl.applicabilityReason = controlDTO.applicabilityReason.idList?.map((a) => Number.parseInt(a)) ?? [];
  newControl.dashboard = controlDTO.dashboard ? mapFromDashboard(controlDTO.dashboard, cache) : undefined;
  newControl.groupId = controlDTO.groupId;
  newControl.ownerId = controlDTO.ownerId;
  newControl.commentTrailId = controlDTO.commentTrailId;
  newControl.auditTrailId = controlDTO.auditTrailId;
  newControl.isoNormIds = cache.norms.filterISOIds(controlDTO.normIds?.idList?.map<number>((i) => Number(i)));
  newControl.customNormIds = cache.norms.filterCustomIds(controlDTO.normIds?.idList?.map<number>((i) => Number(i)));
  newControl.isoControlIds = controlDTO.isoControlIds?.idList?.map<number>((i) => Number(i));
  newControl.trans = mapFromControl_Translation(controlDTO.trans);
  newControl.tasks = controlDTO.tasks ? mapFromTasksArray(controlDTO.tasks, cache) : [];
  newControl.tagIds = controlDTO.tagIds?.idList?.map<number>((i) => Number(i)) ?? [];
  newControl.monitoringParent = controlDTO.monitoringParent ? mapFromEntity(controlDTO.monitoringParent) : undefined;
  newControl.isoControls = controlDTO.isoControls ? mapFromISOControls(controlDTO.isoControls, cache) : undefined;
  newControl.inheritControlId = controlDTO.inheritControlId;
  newControl.inheritTenantId = controlDTO.inheritTenantId;
  newControl.ownerRoleId = controlDTO.ownerRoleId;
  newControl.riskIds = controlDTO.riskIds?.idList?.map<number>((i) => Number(i)) ?? [];
  newControl.latestChangeDateTime = fromApiDateTimeOptional(controlDTO.latestChangeDateTime);
  newControl.unmonitored = controlDTO.unmonitored;
  newControl.perNormData = controlDTO.perNormData ?? false;
  newControl.normControls = controlDTO.normControls?.map(mapFromNormControl);

  if (newControl.trans && newControl.trans.length > 0) {
    newControl.transIdx = 0;
    newControl.name = newControl.trans[0].name;
    newControl.description = newControl.trans[0].description;
    newControl.background = newControl.trans[0].background;
    newControl.implementation = newControl.trans[0].implementation;
    newControl.outOfScopeReason = newControl.trans[0].outOfScopeReason;
  }

  return newControl;
}

export function mapToControl_Translation(
  control_Translations: Control_Translation[] | undefined,
): Control_TranslationDTO[] | undefined {
  if (!control_Translations) {
    return undefined;
  }

  const control_TranslationList: Control_TranslationDTO[] = [];

  for (const control_Translation of control_Translations) {
    const newIControl_TranslationDTO = new Control_TranslationDTO();
    newIControl_TranslationDTO.controlId = control_Translation.controlId;
    newIControl_TranslationDTO.languageId = control_Translation.languageId;
    newIControl_TranslationDTO.name = control_Translation.name;
    newIControl_TranslationDTO.description = control_Translation.description;
    newIControl_TranslationDTO.background = control_Translation.background;
    newIControl_TranslationDTO.implementation = control_Translation.implementation;
    newIControl_TranslationDTO.outOfScopeReason = control_Translation.outOfScopeReason;
    newIControl_TranslationDTO.lang = mapToLanguage(control_Translation.lang);
    control_TranslationList.push(newIControl_TranslationDTO);
  }

  return control_TranslationList;
}

export function mapFromNormControl(normControlDTO: NormControlDTO): NormControl {
  const newNormControl = new NormControl();

  newNormControl.normId = normControlDTO.normId;
  newNormControl.controlId = normControlDTO.controlId;
  newNormControl.ownerId = normControlDTO.ownerId;
  newNormControl.ownerRoleId = normControlDTO.ownerRoleId;
  newNormControl.groupId = normControlDTO.groupId;
  newNormControl.trans = mapFromNormControl_Translation(normControlDTO.trans);

  if (newNormControl.trans && newNormControl.trans.length > 0) {
    newNormControl.transIdx = 0;
    newNormControl.implementation = newNormControl.trans[0].implementation;
  }

  return newNormControl;
}

export function mapToNormControl(normControl: NormControl): NormControlDTO {
  const newNormControlDTO = new NormControlDTO(normControl.normId, normControl.controlId);

  newNormControlDTO.ownerId = normControl.ownerId;
  newNormControlDTO.ownerRoleId = normControl.ownerRoleId;
  newNormControlDTO.groupId = normControl.groupId;
  newNormControlDTO.trans = mapToNormControl_Translation(normControl.trans);

  if (newNormControlDTO.trans && normControl.transIdx >= 0) {
    newNormControlDTO.trans[normControl.transIdx].implementation = normControl.implementation;
  }

  return newNormControlDTO;
}

export function mapFromNormControl_Translation(
  normControl_TranslationDTOs: NormControl_TranslationDTO[] | undefined,
): NormControl_Translation[] | undefined {
  if (!normControl_TranslationDTOs) {
    return undefined;
  }

  const normControl_TranslationList: NormControl_Translation[] = [];

  for (const normControl_TranslationDTO of normControl_TranslationDTOs) {
    const newNormControl_Translation = new NormControl_Translation();
    newNormControl_Translation.normId = normControl_TranslationDTO.normId;
    newNormControl_Translation.controlId = normControl_TranslationDTO.controlId;
    newNormControl_Translation.languageId = normControl_TranslationDTO.languageId;
    newNormControl_Translation.implementation = normControl_TranslationDTO.implementation;
    newNormControl_Translation.lang = mapFromLanguage(normControl_TranslationDTO.lang);
    normControl_TranslationList.push(newNormControl_Translation);
  }

  return normControl_TranslationList;
}

export function mapToNormControl_Translation(
  normControl_Translations: NormControl_Translation[] | undefined,
): NormControl_TranslationDTO[] | undefined {
  if (!normControl_Translations) {
    return undefined;
  }

  const normControl_TranslationList: NormControl_TranslationDTO[] = [];

  for (const normControl_Translation of normControl_Translations) {
    const newNormControl_TranslationDTO = new NormControl_TranslationDTO();
    newNormControl_TranslationDTO.normId = normControl_Translation.normId;
    newNormControl_TranslationDTO.controlId = normControl_Translation.controlId;
    newNormControl_TranslationDTO.languageId = normControl_Translation.languageId;
    newNormControl_TranslationDTO.implementation = normControl_Translation.implementation;
    newNormControl_TranslationDTO.lang = mapToLanguage(normControl_Translation.lang);
    normControl_TranslationList.push(newNormControl_TranslationDTO);
  }

  return normControl_TranslationList;
}

export function mapFromControlContext(controlContextDTO: ControlContextDTO, globalDataCache: GlobalDataCache) {
  const controlContext = new ControlContext();
  controlContext.children = mapFromControls(controlContextDTO.children, globalDataCache);
  controlContext.parent = mapFromControl(controlContextDTO.parent, globalDataCache);
  controlContext.controls = mapFromControls(controlContextDTO.controls, globalDataCache);
  controlContext.themes = mapFromThemes(controlContextDTO.themes, globalDataCache);
  controlContext.processes = mapFromProcesses(controlContextDTO.processes, globalDataCache);
  controlContext.objectives = mapFromObjectives(controlContextDTO.objectives, globalDataCache);
  controlContext.kpis = mapFromKPIs(controlContextDTO.kpis, globalDataCache);
  controlContext.processingActivities = mapFromProcessingActivities(
    controlContextDTO.processingActivities,
    globalDataCache,
  );

  return controlContext;
}

export function mapFromControlScene(controlSceneDTO: ControlSceneDTO, globalDataCache: GlobalDataCache): ControlScene {
  const controlScene = new ControlScene();

  controlScene.controls = mapFromControls(controlSceneDTO.controls, globalDataCache);

  return controlScene;
}

export function mapFromControlDashboard(controlDashboardDTO: ControlDashboardDTO): ControlDashboard {
  const controlDashboard = new ControlDashboard();
  controlDashboard.controlId = controlDashboardDTO.controlId;
  controlDashboard.totalTaskOpenCount = controlDashboardDTO.totalTaskOpenCount;
  controlDashboard.monitoringTaskCount = controlDashboardDTO.monitoringTaskCount;
  controlDashboard.monitoringTaskOpenCount = controlDashboardDTO.monitoringTaskOpenCount;
  controlDashboard.integrationRunCount = controlDashboardDTO.integrationRunCount;
  controlDashboard.integrationRunDeadCount = controlDashboardDTO.integrationRunDeadCount;
  controlDashboard.tagSystemTypeCount = controlDashboardDTO.tagSystemTypeCount;
  controlDashboard.tagOtherCount = controlDashboardDTO.tagOtherCount;

  return controlDashboard;
}
